import MarkdownIt from 'markdown-it'
import TurndownService from 'turndown'
import {
  splitParagraphPlugin,
  withTurndownRule,
  withCustomRender,
  withClickEvent,
} from './markdown.ts'
import { withDynamicInsertSplitMark, registrySplitMarkEvent } from './split.ts'
import { withPaste, insertImage } from './paste.tsx'
import { getSelectionInfo } from './dom.tsx'

interface EventListener {
  onChange?: () => void
  onFocus?: () => void
  onBlur?: () => void
  onSplit?: (splitValue: [string, string]) => void
  onLinkOpen?: (url: string) => void
  onImagePreview?: (url: string) => void
}

interface EditorOptions {
  dom: HTMLElement
  content: string
  editEnable?: boolean
  onImageUpload?: (file: File) => Promise<string>
  enableSplitMark?: boolean
  renderHtml?: boolean
}

export class Editor {
  #config: EditorOptions

  #editor: HTMLDivElement | undefined = undefined

  #turndownService: TurndownService

  #markdownText: string

  listener: EventListener = {}

  #editEnable = true

  #enableSplitMark = true

  #lastFocusSelectionInfo: Record<string, any> | undefined = undefined

  readonly #markdownIt: MarkdownIt

  constructor(options: EditorOptions) {
    this.#config = options
    this.#markdownText = this.#config.content
    this.#editEnable = options.editEnable ?? true
    this.#enableSplitMark = options.enableSplitMark ?? true
    this.#turndownService = new TurndownService({
      headingStyle: 'atx',
    })

    withTurndownRule(this.#turndownService)
    this.#markdownIt = new MarkdownIt({
      linkify: false,
      breaks: true,
      html: options.renderHtml ?? false,
    }).use(splitParagraphPlugin)
  }

  public registryEvents(key: keyof EventListener, callback: any) {
    this.listener[key] = callback
  }

  public reRender(newContent?: string) {
    if (this.#editor) {
      this.#editor.innerHTML = this.#markdownIt.render(
        newContent ?? this.#markdownText,
      )
    }
  }

  public init() {
    const editor = document.createElement('div')
    this.#editor = editor

    editor.contentEditable = this.#editEnable ? 'true' : 'false'
    editor.classList.add('paragraph-editor')
    if (!this.#editEnable) {
      editor.classList.add('disabled')
    }
    editor.innerHTML = this.#markdownIt.render(this.#markdownText)

    // 添加事件
    editor.addEventListener('focus', () => this.listener?.onFocus?.())
    editor.addEventListener('blur', () => this.listener?.onBlur?.())
    editor.addEventListener('input', () => this.listener?.onChange?.())

    editor.addEventListener('mouseup', () => {
      this.#lastFocusSelectionInfo = getSelectionInfo(editor)!
    })

    withPaste(this, this.#config.onImageUpload)
    if (this.#enableSplitMark) {
      registrySplitMarkEvent(this)
      withDynamicInsertSplitMark(this)
      // withLinkEdit(this)
      // 渲染自定义组件
      withCustomRender(this)
      withClickEvent(this)
    }

    this.#config.dom.innerHTML = ''
    this.#config.dom.appendChild(editor)
  }

  public setEditEnable(enable: boolean) {
    this.#editEnable = enable
    if (this.#editor) {
      this.#editor.contentEditable = enable ? 'true' : 'false'
      this.#editor.classList.toggle('disabled', !enable)
    }
  }

  public parseToMarkdown(target: string | TurndownService.Node): string {
    return this.#turndownService.turndown(target)
  }

  public async insertImageIntoContent(editor: Editor['editor'], file: File[]) {
    await insertImage(
      editor!,
      file,
      this.#config.onImageUpload!,
      this.#lastFocusSelectionInfo,
    )
    this.listener.onChange?.()
  }

  get content() {
    if (this.#editor) {
      const value = this.#turndownService.turndown(this.#editor)
      return value.replace(/\u00A0/g, ' ').replace(/\n\s*\n/g, '\n')
    }
    return this.#markdownText
  }

  get textCount() {
    if (this.#editor) {
      const contentTextCount = this.#editor.innerText.trim().length
      const imageCount = this.#editor.querySelectorAll('img').length ?? 0
      return contentTextCount + imageCount * 7 // 图片按7个字符统计
    }
    return this.#markdownText.length
  }

  get editor() {
    return this.#editor
  }

  public destroy() {
    ;(this.#markdownIt as any) = null
  }
}
